// Alta de la empresa a partir del cuestionario de onboarding
// (ADR-2026-07-31-onboarding-nuevo-es-el-flujo-real).
//
// Antes la empresa se creaba en `/onboarding`, un form que pedía nombre, país, marca y
// rubro. Ese form ya no existe: el cuestionario nuevo NO pregunta nada de eso, así que
// los cuatro datos salen de otro lado:
//
//   nombre → del registro (metadata del signUp, o el perfil de Google)
//   marca  → fijo `"Mi marca"` (se cambia después desde la app)
//   país   → deducido: el backend lo saca de la IP del request; acá se manda la
//            heurística por timezone como respaldo (`paisDelBrowser`)
//   rubro  → el del paso 2, traducido de id a label
//
// Se dispara al terminar el **paso 3**, antes del paso de producto: el paso 4 necesita
// `id_company` y créditos para generar de verdad, y así quien abandona a mitad del
// cuestionario no deja una empresa vacía.

import { supabase } from './supabase';
import { createCompanyForUser } from './createCompany';
import { paisDelBrowser } from './paisDelBrowser';
import {
  SETUP_CATEGORIES_KEY,
  SETUP_CONTENT_KEY,
  SETUP_ROLE_KEY,
  rubroDesdeCategorias,
} from '../components/onboarding/accountSetup';

/** Nombre por defecto de la marca. El usuario lo cambia después, desde la app. */
export const MARCA_POR_DEFECTO = 'Mi marca';

export interface AltaResult {
  ok: boolean;
  companyId?: string | null;
  /** Mensaje listo para mostrar. Sólo viene cuando `ok` es false. */
  error?: string;
}

function leerArray(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return Array.isArray(parsed) ? (parsed as string[]).filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * Nombre de la persona. El trigger `handle_new_user` sólo copia id/email/ref_code a
 * `profiles`, así que el nombre hay que buscarlo en la metadata del usuario de auth:
 * `full_name` lo escribe nuestro form de registro y `name` lo trae Google. Si no hay
 * ninguno (registro sin completar el campo), se usa la parte local del mail — nunca
 * queda vacío, porque el saludo de la app y Brevo lo usan.
 */
function nombreDelUsuario(user: {
  email?: string | null;
  user_metadata?: Record<string, unknown> | null;
}): string {
  const meta = user.user_metadata ?? {};
  const candidatos = [meta.full_name, meta.name, meta.nombre];
  for (const c of candidatos) {
    if (typeof c === 'string' && c.trim()) return c.trim();
  }
  const local = (user.email ?? '').split('@')[0];
  return local || 'Usuario';
}

/**
 * Crea la empresa con lo que juntó el cuestionario y persiste las respuestas que
 * tienen columna propia (rol y tipos de contenido). Idempotente del lado del backend:
 * un doble submit no crea una segunda empresa.
 */
export async function crearEmpresaDesdeCuestionario(): Promise<AltaResult> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return { ok: false, error: 'Se cerró la sesión. Volvé a iniciar sesión.' };

  const categorias = leerArray(SETUP_CATEGORIES_KEY);
  const contenidos = leerArray(SETUP_CONTENT_KEY);
  const rol = (() => {
    try {
      return localStorage.getItem(SETUP_ROLE_KEY);
    } catch {
      return null;
    }
  })();

  const res = await createCompanyForUser({
    userId: user.id,
    name: nombreDelUsuario(user),
    // El backend pisa esto con el país deducido de la IP; esto es el respaldo para
    // cuando el servicio de geo no responde. `paisDelBrowser` devuelve null si no
    // puede afirmar nada, y ahí "Otro" es la respuesta honesta.
    pais: paisDelBrowser() ?? 'Otro',
    companyName: MARCA_POR_DEFECTO,
    industry: rubroDesdeCategorias(categorias),
  });
  if (!res.ok) return res;

  // Respuestas del cuestionario que tienen dónde guardarse. Best-effort a propósito:
  // la empresa ya existe y el usuario tiene que poder seguir — un fallo acá se
  // traduce en un dato de segmentación faltante, no en un alta rota.
  try {
    if (rol) await supabase.from('profiles').update({ rol_en_empresa: rol }).eq('id', user.id);
    if (res.companyId && contenidos.length) {
      await supabase
        .from('company_profiles')
        .update({ tipos_contenido: contenidos })
        .eq('id_company', res.companyId);
    }
  } catch {
    /* best-effort */
  }

  return res;
}
